import { action } from "mobx";
import { observer } from "mobx-react-lite";

import { HintExpression } from "@/components/expression/utils/HintExpression";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { NodeKeyboardInput } from "@/components/input/NodeKeyboardInput";
import { commitPendingInput } from "@/components/input/utils/commitPendingInput";
import { isModelNodeInLockedBlock } from "@/components/tiptap/lock/utils";
import { CURSOR_POSITION_CLASS_NAME } from "@/models/CursorModel";
// import { Droppable } from "@/components/dnd/Droppable";
import { InlineNodeModel } from "@/models/nodes/InlineNodeModel";
import { LabelNodeModel } from "@/models/nodes/LabelNodeModel";
import { twMerge } from "@/utils/twMerge";

import { Placeholder } from "../expression/utils/Placeholder";

import { useCursorContextId } from "./CursorContextProvider";

import type { NodeModel } from "@/models/nodes/types";
import type { PlaceholderVisible, PlaceholderAppearance } from "@corca/data";
import type { ComponentProps } from "react";

type UsePlaceholderShownResult = {
  placeholderAppearance: PlaceholderAppearance;
  placeholderVisible: PlaceholderVisible;
  placeholderShown: boolean;
  placeholderIndex?: string;
};

export function usePlaceholderShown(node: NodeModel): UsePlaceholderShownResult {
  const nodeStyle = useNodeStyle();

  if (!node.is(InlineNodeModel)) {
    return {
      placeholderAppearance: nodeStyle.placeholderAppearance,
      placeholderIndex: nodeStyle.placeholderIndex,
      placeholderVisible: "none",
      placeholderShown: false,
    };
  }

  const isInLabelNode = node.ancestors.find(
    (n) => n.is(InlineNodeModel) && n.parent?.is(LabelNodeModel),
  );
  const placeholderVisible = isInLabelNode
    ? nodeStyle.labelPlaceholderVisible
    : nodeStyle.placeholderVisible;

  const placeholderShown =
    (node.isEmpty && placeholderVisible !== "none") ||
    placeholderVisible === "always" ||
    placeholderVisible === "cursored";

  return {
    placeholderAppearance: nodeStyle.placeholderAppearance,
    placeholderIndex: nodeStyle.placeholderIndex,
    placeholderVisible,
    placeholderShown,
  };
}

type CursorAreaProps = ComponentProps<"div"> & {
  node: InlineNodeModel;
};

function CursorArea({ node, ...props }: CursorAreaProps) {
  const { size, view } = useNodeStyle();
  const { placeholderShown, placeholderAppearance, placeholderIndex } = usePlaceholderShown(node);
  const isTextView = view === "text";

  return (
    <div
      className={twMerge(
        "absolute z-10 left-0 right-0 flex flex-1 h-6 cursor-text",
        size === "sm" && "-mx-1.25",
        size === "xs" && "-mx-1",
        size === "xxs" && "-mx-0.75",
        size === "xxxs" && "-mx-0.1",

        placeholderShown && "relative mx-0 h-auto",
        node.is(LabelNodeModel) && placeholderShown && "px-0.5",

        isTextView && size === "xxs" && "-mx-0.5",
      )}
      data-testid="cursor-area"
      {...props}
    >
      {placeholderShown && (
        <Placeholder filled={placeholderAppearance === "filled"} index={placeholderIndex} />
      )}
    </div>
  );
}

type ActiveCursorProps = {
  node: InlineNodeModel;
  position?: "start" | "end";
  handleMouseDown: (e: React.MouseEvent) => void;
  handleMouseOver: (e: React.MouseEvent) => void;
};

const ActiveCursor = observer(function ActiveCursor({
  node,
  position,
  handleMouseDown,
  handleMouseOver,
}: ActiveCursorProps) {
  const { doc } = node;
  const { size, view } = useNodeStyle();
  const { placeholderShown, placeholderVisible, placeholderAppearance, placeholderIndex } =
    usePlaceholderShown(node);

  const { cursorHidden } = doc.cursor;
  const isEditing = !!doc.input.editingNode;
  const isTextView = view === "text";

  const previewNode = doc.isFocused ? doc.editor.hints.cursorPreviewNode : null;

  const caret =
    !isEditing && doc.editable && !isModelNodeInLockedBlock(node) ? (
      <>
        <NodeKeyboardInput
          doc={doc}
          node={node}
          className={twMerge(
            placeholderShown && "mx-auto",
            cursorHidden && "opacity-0",
            !placeholderShown && position === "end" && "ml-auto",
          )}
        />
        {placeholderVisible === "cursored" && doc.input.value.length < 2 && (
          <div
            className={twMerge(
              "absolute left-0 right-0 flex items-center pointer-events-none",
              isTextView && size === "xxs" && "-mx-0.5",
            )}
          >
            <Placeholder filled={placeholderAppearance === "filled"} index={placeholderIndex} />
          </div>
        )}
      </>
    ) : (
      <CursorArea node={node} onMouseDown={handleMouseDown} onMouseOver={handleMouseOver} />
    );

  return (
    <>
      {previewNode && (
        <div className="flex items-baseline pl-px" data-testid="cursor-hint-preview">
          <HintExpression node={previewNode} />
        </div>
      )}
      {caret}
    </>
  );
});

type Props = {
  node: InlineNodeModel;
  index?: number;
  position?: "start" | "end";
  className?: string;
};

export const Cursor = observer(function Cursor({ node, index, position, className }: Props) {
  const { doc, id } = node;

  const { size } = useNodeStyle();
  const cursorContextId = useCursorContextId();
  const { placeholderShown } = usePlaceholderShown(node);

  const isCurrent = doc.cursorContextId === cursorContextId && doc.cursor.isCurrent(node, index);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button === 2) {
      e.preventDefault();
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const adjustIndex = commitPendingInput(doc);

    if (e.shiftKey && index !== undefined) {
      action(() => {
        doc.cursorContextId = cursorContextId;
        doc.editor.resetInput(true);
        doc.selection.expandRangeToInlinePosition(node, adjustIndex(node, index) ?? index);
        doc.cursor.hideCursor();
      })();
      return;
    }

    action(() => {
      doc.cursorContextId = cursorContextId;

      doc.editor.resetInput(true);
      doc.cursor.moveTo(node, adjustIndex(node, index));
      doc.selection.resetSelection();
      doc.selection.startPointerSelection();
    })();
  };

  const handleMouseOver = action((e: React.MouseEvent) => {
    if (index !== undefined && doc.selection.updatePointerSelection(node, index)) {
      e.preventDefault();
      e.stopPropagation();
    }
  });

  // TODO: return collaborative cursor https://linear.app/corca-research/issue/D-1264/return-collaborative-cursor
  // const cursorState = doc.awareness?.cursorState(node.id, index);

  return (
    <div
      className={twMerge(
        CURSOR_POSITION_CLASS_NAME,
        "relative z-50 flex flex-1 w-full h-full flex-nowrap items-center",

        // preserves height/baseline for correct vertical positioning
        "before:content-zero-space",
        // "outline outline-blue-300",

        size === "sm" && placeholderShown && "min-w-[21px]",
        size === "xs" && placeholderShown && "min-w-4",
        size === "xxs" && placeholderShown && "min-w-3.5",
        className,
      )}
      data-node-id={id}
      data-cursor-index={index}
      data-cursor-position={position}
    >
      {/*
        Drag & drop is temporary disabled.
        The work will be tracked here: D-359
        <Droppable node={node} index={index} />
      */}
      {isCurrent ? (
        <ActiveCursor
          node={node}
          position={position}
          handleMouseDown={handleMouseDown}
          handleMouseOver={handleMouseOver}
        />
      ) : (
        <CursorArea node={node} onMouseDown={handleMouseDown} onMouseOver={handleMouseOver} />
      )}
    </div>
  );
});
